<template>
  <div class="p-field" :class="{ 'p-float-label': labelStyle === 'float' }">
    <div :class="{ 'p-field-horizontal': labelStyle === 'inline' }">
      <!-- Label -->
      <label v-if="label" :for="id" class="label-text">
        {{ label }}
        <span v-if="required" class="text-red-500">*</span>
      </label>

      <!-- Input Wrapper -->
      <div :class="{ 'p-input-wrapper relative': hasPrefix || hasSuffix }">
        <div class="p-input-container">
          <div v-if="hasPrefix"
            class="absolute left-3 top-1/2 -translate-y-1/2 flex items-center z-10 w-5 justify-center">
            <slot name="prefix"></slot>
          </div>
          <div v-if="hasSuffix || (clearable && isFilled)"
            class="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2 z-10">
            <div v-if="hasSuffix">
              <slot name="suffix"></slot>
            </div>
            <div v-if="clearable && isFilled">
              <i class="pi pi-times cursor-pointer" @click="clear"></i>
            </div>
          </div>
          <InputMask ref="inputRef" :id="id" v-model="model" :mask="mask" :slotChar="slotChar" :autoClear="autoClear"
            :unmask="unmask" :class="[
              'p-inputtext p-component w-full',
              {
                'p-filled': isFilled,
                'p-inputtext-sm': size === 'small',
                'p-inputtext-lg': size === 'large',
                'p-invalid': isInvalid,
                'p-disabled': disabled,
                [`p-variant-${variant}`]: variant,
                'pl-9': hasPrefix,
                'pr-9': hasSuffix || (clearable && isFilled)
              }
            ]" :required="required" :disabled="disabled" :placeholder="placeholder" :readonly="readonly"
            :aria-label="props['aria-label']" :aria-labelledby="props['aria-labelledby']" :aria-invalid="!!isInvalid"
            :aria-required="required" :aria-describedby="ariaDescribedBy" v-bind="$attrs" @update:modelValue="onInput"
            @complete="onComplete" @focus="onFocus" @blur="onBlur" @change="onChange" />
        </div>
      </div>
    </div>

    <!-- Error Messages -->
    <div v-if="showErrorMessages" class="p-error-messages">
      <small v-if="helperText" :id="helperTextId" class="p-error block">{{ helperText }}</small>
      <small v-if="shouldShowError" :id="`${id}-error`" class="p-error block text-red-500">
        {{ getFormattedErrorMessage }}
      </small>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import InputMask from 'primevue/inputmask'

export default defineComponent({
  name: 'ONEInputMask',
  components: {
    InputMask
  },
  inheritAttrs: false
})
</script>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useVeeValidate } from '../../../../composables/useVeeValidate'

export interface Props {
  modelValue?: string | null
  mask: string
  slotChar?: string
  autoClear?: boolean
  unmask?: boolean
  size?: 'small' | 'large'
  invalid?: boolean
  disabled?: boolean
  readonly?: boolean
  required?: boolean
  placeholder?: string
  helperText?: string
  label?: string
  labelStyle?: 'fixed' | 'float' | 'inline'
  'aria-label'?: string
  'aria-labelledby'?: string
  id?: string
  variant?: 'outlined' | 'filled'
  clearable?: boolean
  name: string
  hasPrefix?: boolean
  hasSuffix?: boolean
  validationType?: 'email' | 'phone' | 'password' | 'url' | 'numeric' | 'alpha' | 'alphanumeric'
  customRules?: Record<string, (value: any) => boolean | string>
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  slotChar: '_',
  autoClear: true,
  unmask: false,
  size: undefined,
  invalid: false,
  disabled: false,
  readonly: false,
  required: false,
  placeholder: '',
  helperText: '',
  label: '',
  labelStyle: 'fixed',
  'aria-label': '',
  'aria-labelledby': '',
  id: () => `one-inputmask-${Math.random().toString(36).substr(2, 9)}`,
  variant: 'outlined',
  clearable: false,
  hasPrefix: false,
  hasSuffix: false,
  validationType: undefined,
  customRules: undefined
})

const emit = defineEmits<{
  'update:modelValue': [value: string | null]
  'input': [event: Event]
  'focus': [event: Event]
  'blur': [event: Event]
  'change': [event: Event]
  'complete': [event: Event]
  'clear': []
}>()

// Local State
const inputRef = ref<HTMLInputElement | null>(null)
const isFocused = ref(false)
const touched = ref(false)

// Local model for v-model binding
const model = ref<string>(props.modelValue?.toString() || '')

// Watch for external modelValue changes
watch(() => props.modelValue, (newVal) => {
  model.value = newVal?.toString() || ''
}, { immediate: true })

// Setup VeeValidate
const {
  fieldValue,
  errorMessage,
  shouldShowError,
  validate,
  handleValidation,
  updateValue,
  isEmpty
} = useVeeValidate({
  name: props.name,
  modelValue: model.value,
  required: props.required,
  validationType: props.validationType,
  customRules: props.customRules
})

// Watch fieldValue changes to update model
watch(() => fieldValue.value, (newVal) => {
  model.value = newVal?.toString() || ''
}, { immediate: true })

const isFilled = computed(() => {
  return model.value != null && model.value !== ''
})

const isInvalid = computed(() => {
  return props.invalid || shouldShowError.value
})

const helperTextId = computed(() => `${props.id}-helper`)

const ariaDescribedBy = computed(() => {
  const ids = []
  if (props.helperText) ids.push(helperTextId.value)
  if (shouldShowError.value) ids.push(`${props.id}-error`)
  return ids.join(' ')
})

const showErrorMessages = computed(() => {
  return !!props.helperText || shouldShowError.value
})

// Format error message based on validation type
const getFormattedErrorMessage = computed(() => {
  if (!errorMessage.value) return ''

  if (props.required && isEmpty(model.value)) {
    return `${props.name} is required`
  }

  switch (props.validationType) {
    case 'email':
      return 'Please enter a valid email address'
    case 'phone':
      return 'Please enter a valid phone number'
    case 'password':
      return 'Password must contain at least 8 characters, including uppercase, lowercase, number and special character'
    case 'url':
      return 'Please enter a valid URL'
    case 'numeric':
      return 'Please enter numbers only'
    case 'alpha':
      return 'Please enter letters only'
    case 'alphanumeric':
      return 'Please enter letters and numbers only'
    default:
      return errorMessage.value
  }
})

const onInput = async (value: string) => {
  updateValue(value)
  emit('update:modelValue', value || null)
  emit('input', new Event('input'))

  if (shouldShowError.value) {
    await validate()
  }
}

const onComplete = (event: Event) => {
  emit('complete', event)
}

const onFocus = (event: Event) => {
  isFocused.value = true
  emit('focus', event)
}

const onBlur = async (event: Event) => {
  isFocused.value = false
  touched.value = true
  emit('blur', event)
  await handleValidation()
}

const onChange = async (event: Event) => {
  emit('change', event)
  await validate()
}

const clear = () => {
  model.value = ''
  updateValue('')
  emit('update:modelValue', null)
  emit('clear')
}

const focus = () => {
  inputRef.value?.focus()
}

// Expose methods for parent form validation
defineExpose({
  focus,
  blur: () => inputRef.value?.blur(),
  clear,
  validate,
  errorMessage,
  isValid: computed(() => !isInvalid.value)
})
</script>
